/**
 * src/components/AddressAutocomplete.tsx — Google Places address search.
 *
 * Debounced predictions from our server proxy (/PlacesAutocomplete); on pick,
 * resolves the full address + coordinates + serviceability (/PlaceDetails) and
 * hands the parent a PlaceResolved. A session token is held for the lifetime
 * of one search→pick and rotated afterward (Google session billing).
 *
 * The key never reaches the client — everything goes through Harper.
 */

import { useEffect, useRef, useState } from 'react';
import { MapPin, Search } from './icons';
import {
  placesAutocomplete, placeDetails, newPlacesSessionToken,
  type PlacePrediction, type PlaceResolved,
} from '../api/placesApi';

interface Props {
  onResolved: (place: PlaceResolved) => void;
  disabled?: boolean;
  placeholder?: string;
  testId?: string;
}

export function AddressAutocomplete({ onResolved, disabled, placeholder, testId }: Props) {
  const [query, setQuery] = useState('');
  const [preds, setPreds] = useState<PlacePrediction[]>([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const tokenRef = useRef<string>(newPlacesSessionToken());
  const seqRef = useRef(0);
  const justSelectedRef = useRef(false);

  useEffect(() => {
    const q = query.trim();
    if (justSelectedRef.current) { justSelectedRef.current = false; return; }
    if (q.length < 3) { setPreds([]); setOpen(false); return; }
    const seq = ++seqRef.current;
    setBusy(true);
    const timer = setTimeout(async () => {
      try {
        const results = await placesAutocomplete(q, tokenRef.current);
        if (seq !== seqRef.current) return; // a newer keystroke superseded this
        setPreds(results);
        setOpen(true);
      } catch {
        if (seq === seqRef.current) setPreds([]);
      } finally {
        if (seq === seqRef.current) setBusy(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [query]);

  const select = async (p: PlacePrediction) => {
    justSelectedRef.current = true;
    setQuery(p.mainText);
    setOpen(false);
    setBusy(true);
    try {
      const resolved = await placeDetails(p.placeId, tokenRef.current);
      onResolved(resolved);
    } catch {
      /* parent stays in unresolved state; user can retry or go manual */
    } finally {
      setBusy(false);
      tokenRef.current = newPlacesSessionToken(); // close the billing session
    }
  };

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <div style={{ position: 'relative' }}>
        <Search size={16} aria-hidden="true" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-muted)', pointerEvents: 'none' }} />
        <input
          type="text"
          data-testid={testId ?? 'address-autocomplete-input'}
          value={query}
          disabled={disabled}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => { if (preds.length > 0) setOpen(true); }}
          placeholder={placeholder ?? 'Start typing your address…'}
          autoComplete="off"
          aria-autocomplete="list"
          style={{
            width: '100%', height: 46, padding: '0 12px 0 36px',
            border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)',
            fontSize: 'var(--fs-sm)', backgroundColor: 'var(--color-surface)',
            color: 'var(--color-text-primary)', boxSizing: 'border-box',
          }}
        />
        {busy && (
          <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', width: 16, height: 16, border: '2px solid var(--color-primary)', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} aria-hidden="true" />
        )}
      </div>

      {open && preds.length > 0 && (
        <ul
          role="listbox"
          style={{
            position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 10,
            listStyle: 'none', margin: 0, padding: 4,
            background: 'var(--color-surface)', border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-2)',
            maxHeight: 260, overflowY: 'auto',
          }}
        >
          {preds.map((p) => (
            <li key={p.placeId} role="option" aria-selected={false}>
              <button
                type="button"
                data-testid="address-autocomplete-option"
                onMouseDown={(e) => { e.preventDefault(); void select(p); }}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 8, width: '100%',
                  textAlign: 'left', background: 'transparent', border: 'none',
                  padding: '10px 10px', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                  color: 'var(--color-text-primary)', fontFamily: 'inherit',
                }}
              >
                <MapPin size={15} color="var(--color-text-muted)" aria-hidden="true" style={{ marginTop: 2, flexShrink: 0 }} />
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 'var(--fs-sm)', fontWeight: 500 }}>{p.mainText}</span>
                  <span style={{ display: 'block', fontSize: 'var(--fs-xs)', color: 'var(--color-text-muted)' }}>{p.secondaryText}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
